/**
 * Motor del orbe neuronal, compartido.
 *
 * Canvas 2D puro, sin librerías y sin WebGL: nodos repartidos sobre una esfera
 * (espiral de Fibonacci) que rota, aristas tejidas por vecindad, y señales de luz
 * que viajan de nodo en nodo y encienden el que alcanzan. Detrás, una nébula lenta
 * de dos nubes. Lo usan dos escenas con la misma red y distinta paleta:
 *
 *  - `timbrando`: la espera de "Timbrando tu CFDI" (cfdi/timbrando-orb.js), 78 nodos.
 *  - `cristal`: el avance del asistente de migración (migracion/orbe-migracion.js),
 *    92 nodos dentro de una esfera de cristal. Referencia visual aprobada:
 *    docs/superpowers/mockups/2026-10-20-asistente-migracion/orbe.html (variante A).
 *
 * Todo lo que distingue a una escena de la otra vive en `PALETAS` (colores y cifras
 * de forma); el dibujo es uno solo. Con la paleta `timbrando` el dibujo es idéntico,
 * llamada por llamada, al que tenía timbrando-orb.js antes de extraer el motor.
 *
 * `cristal` además ata la red al avance: cada nodo tiene un umbral fijo (determinista
 * por semilla) y `encender(progreso)` lo marca como aprendido cuando el avance lo
 * alcanza. Un nodo aprendido ya no se apaga: la red se va llenando. `rafaga()` y
 * `destello()` son los acentos de los eventos (llegó un bloque de CFDIs, terminó un
 * renglón).
 *
 * Este módulo no toca el DOM al importarse: los sprites se crean en el primer
 * `dibujar()`, no al construir el motor, así que la parte pura se prueba en `node`.
 */

// ------------------------------------------------------------------ constantes

var AUREO = 2.399963229728653; // ángulo áureo, en radianes
var VECINOS = 3;               // aristas por nodo antes de deduplicar el par
var TOPE_PULSOS = 120;         // red de seguridad: ráfagas repetidas no acumulan sin fin

// Familia de color de la escena "Timbrando" (la misma que el resplandor CSS de
// _cfdi-crear.scss).
var C = {
  azul:    [47, 107, 255],
  cian:    [53, 208, 255],
  violeta: [123, 92, 255],
  blanco:  [214, 234, 255]
};

/**
 * Paletas. Cada una trae:
 *  - colores: `arista`, `nodo` (sprite del nodo apagado), `vivo` (sprite del nodo
 *    recién encendido), `pulso` (señales), `aprendido` (nodo ya encendido; null si la
 *    escena no tiene la idea). Opcionales: `punto` (centro del nodo apagado, por
 *    omisión `nodo`) y `puntoVivo` (centro del nodo encendido, por omisión `aprendido`).
 *  - `modo` (composición del dibujo) y `radio` (de la esfera, contra el lado del lienzo).
 *  - la forma: cifras de giro, respiración, halo, nébula, trazo, señales y nodos.
 *    Los pares [a, b] son "base + pendiente * profundidad" (o * fogo) como en el dibujo.
 */
export var PALETAS = {
  timbrando: {
    nodos: 78,
    azul: C.azul, cian: C.cian, violeta: C.violeta, blanco: C.blanco,
    arista: C.azul, nodo: C.azul, punto: C.cian, vivo: C.blanco, puntoVivo: C.blanco,
    pulso: C.cian, aprendido: null,
    modo: 'lighter',
    radio: 0.335,
    giro: { y: 0.18, x0: 0.30, xAmp: 0.07, xVel: 0.21 },
    respira: { amp: 0.035, vel: 0.7 },
    halo: { centro: C.azul, medio: C.violeta },
    nubes: [C.cian, C.violeta],
    nube: { alfa: 0.13, radio: 0.95 },
    trazoAlfa: [0.05, 0.22],
    trazoAncho: [0.55, 0.75],
    decae: 3.2,
    senales: { iniciales: 14, minimos: 12, sigue: 0.92, llegada: 1, tam: 13, paso: 1.9 },
    luz: [0.62, 0.16],
    umbralFogo: 0.04,
    nodoTam: { base: 7.5, aprendido: 7.5, fogo: 17 },
    nodoAlfa: [0.30, 0.45, 0.55],
    puntoAlfa: [0.55, 0.45],
    puntoRelleno: [0.55, 0.45],
    puntoRadio: { base: 0.9, aprendido: 0.9, fogo: 1.2 }
  },

  // Esfera de cristal del asistente de migración (orbe.html, variante A "lente").
  // El fondo oscuro y el brillo de la esfera son CSS de la vista; aquí solo la red.
  cristal: {
    nodos: 92,
    arista: [96, 165, 250], nodo: [56, 189, 248], vivo: [224, 242, 254],
    pulso: [125, 211, 252], aprendido: [165, 243, 252],
    modo: 'lighter',
    radio: 0.36,
    giro: { y: 0.2, x0: 0.32, xAmp: 0.08, xVel: 0.23 },
    respira: { amp: 0.03, vel: 0.72 },
    halo: null,
    nubes: [C.cian, C.violeta],
    nube: { alfa: 0.12, radio: 1 },
    trazoAlfa: [0.06, 0.22],
    trazoAncho: [0.55, 0.8],
    decae: 2.6,
    senales: { iniciales: 12, minimos: 10, sigue: 0.9, llegada: 0.8, tam: 14, paso: 2 },
    luz: [0.6, 0.15],
    umbralFogo: 0.05,
    nodoTam: { base: 7, aprendido: 11, fogo: 16 },
    nodoAlfa: [0.28, 0.45, 0.5],
    puntoAlfa: [0.5, 0.5],
    puntoRelleno: [0.6, 0.4],
    puntoRadio: { base: 0.95, aprendido: 1.5, fogo: 1.3 }
  }
};

// --------------------------------------------------------------- utilería pura

function tope255(v) {
  return v < 0 ? 0 : (v > 255 ? 255 : v);
}

/**
 * Color de la paleta con la saturación y el brillo del momento aplicados.
 * `fx` es {sat, bri}; con sat=1 y bri=1 (lo normal) devuelve el color tal cual.
 */
export function rgba(c, a, fx) {
  var sat = fx && fx.sat != null ? fx.sat : 1;
  var bri = fx && fx.bri != null ? fx.bri : 1;
  var l = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  var r = tope255((l + (c[0] - l) * sat) * bri);
  var g = tope255((l + (c[1] - l) * sat) * bri);
  var b = tope255((l + (c[2] - l) * sat) * bri);
  return 'rgba(' + (r | 0) + ',' + (g | 0) + ',' + (b | 0) + ',' + a + ')';
}

/**
 * Nodos sobre la esfera (espiral de Fibonacci: reparto parejo sin polos apelmazados)
 * y aristas por vecindad, sin repetir el par. Cada nodo guarda en `vec` los índices
 * de sus aristas para que una señal pueda saltar a la siguiente al llegar.
 *
 * Pura y determinista: la misma n da siempre la misma red (de ahí que se pueda
 * probar sin DOM).
 */
export function construirRed(n) {
  var nodos = [], aristas = [], vistos = {}, i, j, k;

  for (i = 0; i < n; i++) {
    var y = n > 1 ? 1 - (i / (n - 1)) * 2 : 0;
    var rad = Math.sqrt(Math.max(0, 1 - y * y));
    var th = i * AUREO;
    nodos.push({ x: Math.cos(th) * rad, y: y, z: Math.sin(th) * rad, fogo: 0, vec: [] });
  }

  for (i = 0; i < n; i++) {
    var lista = [];
    for (j = 0; j < n; j++) {
      if (j === i) continue;
      var dx = nodos[i].x - nodos[j].x;
      var dy = nodos[i].y - nodos[j].y;
      var dz = nodos[i].z - nodos[j].z;
      lista.push([dx * dx + dy * dy + dz * dz, j]);
    }
    lista.sort(function (a, b) { return a[0] - b[0]; });
    for (k = 0; k < VECINOS && k < lista.length; k++) {
      var o = lista[k][1];
      var a = Math.min(i, o), b = Math.max(i, o);
      var clave = a + ':' + b;
      if (vistos[clave]) continue;
      vistos[clave] = 1;
      aristas.push({ a: a, b: b });
    }
  }

  for (i = 0; i < aristas.length; i++) {
    nodos[aristas[i].a].vec.push(i);
    nodos[aristas[i].b].vec.push(i);
  }

  return { nodos: nodos, aristas: aristas };
}

/**
 * Una señal nueva saliendo de `desde` (o de un nodo al azar si viene null) por una
 * de sus aristas. `azar` se puede inyectar para probarla sin aleatoriedad.
 * Devuelve null si el nodo quedó aislado.
 */
export function crearPulso(nodos, aristas, desde, azar) {
  var rnd = azar || Math.random;
  var o = (desde == null) ? (rnd() * nodos.length | 0) : desde;
  var nodo = nodos[o];
  if (!nodo || !nodo.vec.length) return null;
  var ar = aristas[nodo.vec[rnd() * nodo.vec.length | 0]];
  return { ini: o, fin: (ar.a === o ? ar.b : ar.a), p: 0, v: 0.60 + rnd() * 0.55 };
}

/** mulberry32: PRNG de 32 bits, determinista por semilla, con valores en [0,1). */
function mulberry32(semilla) {
  var a = semilla | 0;
  return function () {
    a = (a + 0x6D2B79F5) | 0;
    var t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Umbral de cada nodo: el avance (0 a 1) a partir del cual se enciende. La misma
 * semilla da siempre los mismos umbrales, así que la misma cuenta ve llenarse su
 * red en el mismo orden cada vez. Valores en [0,1): con el avance en 1 todos
 * quedan encendidos.
 */
export function umbralesNodos(n, semilla) {
  var azar = mulberry32(semilla);
  var u = [];
  for (var i = 0; i < n; i++) u.push(azar());
  return u;
}

// --------------------------------------------------------------- utilería 2D

/** Sprite de luz radial de 64px: pintar un degradado por nodo sería carísimo. */
function spriteLuz(color, fx, luz) {
  var c = document.createElement('canvas');
  c.width = c.height = 64;
  var g = c.getContext('2d');
  var gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0.00, rgba(color, 1, fx));
  gr.addColorStop(0.16, rgba(color, luz[0], fx));
  gr.addColorStop(0.42, rgba(color, luz[1], fx));
  gr.addColorStop(1.00, rgba(color, 0, fx));
  g.fillStyle = gr;
  g.fillRect(0, 0, 64, 64);
  return c;
}

/**
 * Ajusta el búfer del canvas al tamaño en CSS por el devicePixelRatio (topado) y
 * deja el contexto trabajando en unidades CSS. Devuelve el lado en px CSS: el
 * escenario es cuadrado, así que todo se escala contra ese lado.
 */
function ajustar2D(canvas, ctx, tope) {
  var dpr = Math.min(window.devicePixelRatio || 1, tope);
  var w = Math.max(1, canvas.clientWidth);
  var h = Math.max(1, canvas.clientHeight);
  var bw = Math.round(w * dpr), bh = Math.round(h * dpr);
  if (canvas.width !== bw || canvas.height !== bh) { canvas.width = bw; canvas.height = bh; }
  ctx.setTransform(bw / w, 0, 0, bh / h, 0, 0);
  return w;
}

// ------------------------------------------------------------------- el motor

/**
 * Devuelve el motor de la red, o null si no hay contexto 2D.
 *
 *   opciones.paleta  'timbrando' (por omisión) | 'cristal'
 *   opciones.nodos   cuántos nodos; por omisión los de la paleta (78 y 92)
 *   opciones.semilla semilla de los umbrales de `encender` (por omisión 0)
 *   opciones.calma   reduced-motion desde el montaje: sin él, un `encender` o una
 *                    `rafaga` antes del primer cuadro sembrarían señales (R25 a)
 *
 *   dibujar(esc)     pinta un cuadro; esc = {t, dt, real, calma, fx, tope}
 *   encender(p, silencioso)
 *                    marca como aprendidos los nodos con umbral <= p (p de 0 a 1) y
 *                    devuelve los índices recién encendidos. Monótono: no apaga. En
 *                    silencio (la primera sincronización al montar, R25 b) los deja
 *                    encendidos sin destello ni señales.
 *   rafaga()         siembra 3 señales desde un nodo aprendido
 *   destello()       enciende `fogo` = 1 en todos los nodos aprendidos
 *
 * Con `calma` (prefers-reduced-motion) no hay señales: ni las que dibuja `dibujar`
 * ni las que sembrarían `encender` y `rafaga`.
 */
export function crearMotor(canvas, opciones) {
  var op = opciones || {};
  var p = PALETAS[op.paleta] || PALETAS.timbrando;

  var ctx = null;
  try { ctx = canvas.getContext('2d'); } catch (e) { ctx = null; }
  if (!ctx) return null;

  var red = construirRed(op.nodos > 0 ? (op.nodos | 0) : p.nodos);
  var nodos = red.nodos, aristas = red.aristas, N = nodos.length;
  var pulsos = [];
  var aprendidos = [];     // índices de los nodos ya encendidos, en el orden en que se encendieron
  var calma = !!op.calma;  // lo fija cada cuadro (y el montaje); encender() y rafaga() lo consultan
  var sen = p.senales;
  var cPunto = p.punto || p.nodo;
  var cPuntoVivo = p.puntoVivo || p.aprendido;
  var i;

  var umbrales = umbralesNodos(N, op.semilla);
  for (i = 0; i < N; i++) { nodos[i].umbral = umbrales[i]; nodos[i].on = false; }

  function sembrar(desde) {
    if (pulsos.length >= TOPE_PULSOS) return;
    var pu = crearPulso(nodos, aristas, desde, null);
    if (pu) pulsos.push(pu);
  }
  for (i = 0; i < sen.iniciales; i++) sembrar(null);

  // Los sprites sólo se rehacen cuando cambia la intensidad (fx.version), no cada
  // cuadro: la atenuación del estado resuelto avanza por escalones, no continua.
  var cache = { ver: -1, nodo: null, vivo: null, pulso: null, aprendido: null };
  var px = new Float32Array(N), py = new Float32Array(N);
  var pz = new Float32Array(N), ps = new Float32Array(N);
  var orden = [];
  for (i = 0; i < N; i++) orden.push(i);

  function porProfundidad(a, b) { return pz[a] - pz[b]; }

  function dibujar(esc) {
    var t = esc.t, dt = esc.dt, fx = esc.fx;
    var L = ajustar2D(canvas, ctx, esc.tope);
    var esc300 = L / 300;                       // todo se calibró a un orb de 300px
    var i, k, n;

    calma = !!esc.calma;
    ctx.clearRect(0, 0, L, L);

    if (cache.ver !== fx.version) {
      cache.ver = fx.version;
      cache.pulso = spriteLuz(p.pulso, fx, p.luz);
      cache.nodo = spriteLuz(p.nodo, fx, p.luz);
      cache.vivo = spriteLuz(p.vivo, fx, p.luz);
      cache.aprendido = p.aprendido ? spriteLuz(p.aprendido, fx, p.luz) : null;
    }

    // Con reduced-motion la esfera casi no gira: el movimiento es un pulso lento de
    // respiración, medido contra el reloj real (el de la escena va frenado).
    var respira = esc.calma ? 1 + p.respira.amp * Math.sin(esc.real * p.respira.vel) : 1;
    var cx = L / 2, cy = L / 2, R = L * p.radio * respira, foco = 3.4;
    var ay = t * p.giro.y, ax = p.giro.x0 + p.giro.xAmp * Math.sin(t * p.giro.xVel);
    var cy1 = Math.cos(ay), sy1 = Math.sin(ay), cx1 = Math.cos(ax), sx1 = Math.sin(ax);

    // Halo de fondo (sólo si la escena lo trae: la esfera de cristal ya tiene el suyo
    // en CSS) y dos nubes lentas, para que la red no flote en el vacío.
    if (p.halo) {
      var gr = ctx.createRadialGradient(cx, cy, R * 0.05, cx, cy, R * 1.65);
      gr.addColorStop(0, rgba(p.halo.centro, 0.26, fx));
      gr.addColorStop(0.45, rgba(p.halo.medio, 0.10, fx));
      gr.addColorStop(1, rgba(p.halo.centro, 0, fx));
      ctx.fillStyle = gr;
      ctx.fillRect(0, 0, L, L);
    }

    ctx.globalCompositeOperation = p.modo;
    for (k = 0; k < 2; k++) {
      var fa = t * (0.13 + k * 0.09) + k * 2.1;
      var nx = cx + Math.cos(fa) * R * 0.34, ny = cy + Math.sin(fa * 0.83) * R * 0.30;
      var g2 = ctx.createRadialGradient(nx, ny, 0, nx, ny, R * p.nube.radio);
      g2.addColorStop(0, rgba(p.nubes[k], p.nube.alfa, fx));
      g2.addColorStop(1, rgba(p.nubes[k], 0, fx));
      ctx.fillStyle = g2;
      ctx.fillRect(0, 0, L, L);
    }

    // Proyección de la esfera: rotación en Y, inclinación en X y perspectiva.
    for (i = 0; i < N; i++) {
      n = nodos[i];
      var x1 = n.x * cy1 + n.z * sy1;
      var z1 = -n.x * sy1 + n.z * cy1;
      var y2 = n.y * cx1 - z1 * sx1;
      var z2 = n.y * sx1 + z1 * cx1;
      var s = foco / (foco - z2);
      px[i] = cx + x1 * R * s;
      py[i] = cy - y2 * R * s;
      pz[i] = z2;
      ps[i] = s;
      n.fogo *= Math.exp(-dt * p.decae);
    }

    // Aristas en 4 cubetas de profundidad: 4 stroke() en vez de uno por arista.
    for (var cub = 0; cub < 4; cub++) {
      var prof = (cub + 0.5) / 4;
      ctx.beginPath();
      for (i = 0; i < aristas.length; i++) {
        var ar = aristas[i];
        var q = ((pz[ar.a] + pz[ar.b]) * 0.5 + 1) / 2;
        if ((q * 4 | 0) !== cub) continue;
        ctx.moveTo(px[ar.a], py[ar.a]);
        ctx.lineTo(px[ar.b], py[ar.b]);
      }
      ctx.strokeStyle = rgba(p.arista, p.trazoAlfa[0] + prof * p.trazoAlfa[1], fx);
      ctx.lineWidth = (p.trazoAncho[0] + prof * p.trazoAncho[1]) * esc300;
      ctx.stroke();
    }

    // Señales viajando por las aristas (apagadas con reduced-motion).
    if (!esc.calma) {
      for (i = pulsos.length - 1; i >= 0; i--) {
        var pu = pulsos[i];
        pu.p += pu.v * dt;
        var ini = pu.ini, fin = pu.fin;
        if (pu.p >= 1) {
          nodos[fin].fogo = Math.max(nodos[fin].fogo, sen.llegada);  // el nodo alcanzado se enciende
          pulsos.splice(i, 1);
          if (Math.random() < sen.sigue) sembrar(fin);               // casi siempre sigue de largo
          if (pulsos.length < sen.minimos) sembrar(null);
          continue;
        }
        for (k = 0; k < 5; k++) {                    // estela de 5 destellos
          var pp = pu.p - k * 0.055;
          if (pp < 0) break;
          var ux = px[ini] + (px[fin] - px[ini]) * pp;
          var uy = py[ini] + (py[fin] - py[ini]) * pp;
          var uz = pz[ini] + (pz[fin] - pz[ini]) * pp;
          var vis = 0.35 + 0.65 * ((uz + 1) / 2);    // lo de atrás se ve más tenue
          var tam = (sen.tam - k * sen.paso) * esc300 * vis;
          ctx.globalAlpha = (1 - k / 5) * 0.85 * vis;
          ctx.drawImage(cache.pulso, ux - tam / 2, uy - tam / 2, tam, tam);
        }
      }
      ctx.globalAlpha = 1;
    }

    // Nodos, de atrás hacia adelante para que los de enfrente tapen a los de atrás.
    // Apagado, aprendido (ya encendido) o con fogo (recién alcanzado por una señal).
    orden.sort(porProfundidad);
    for (var oi = 0; oi < N; oi++) {
      i = orden[oi];
      var vv = 0.30 + 0.70 * ((pz[i] + 1) / 2);
      var fo = nodos[i].fogo, on = nodos[i].on;
      var vivo = fo > p.umbralFogo;
      var tg = ((on ? p.nodoTam.aprendido : p.nodoTam.base) + fo * p.nodoTam.fogo) * esc300 * ps[i] * vv;
      ctx.globalAlpha = p.nodoAlfa[0] + p.nodoAlfa[1] * vv + fo * p.nodoAlfa[2];
      // Timbrando no tiene sprite de aprendido: un nodo encendido ahí se pinta como nodo (R25 c).
      ctx.drawImage(vivo ? cache.vivo : (on ? (cache.aprendido || cache.nodo) : cache.nodo), px[i] - tg / 2, py[i] - tg / 2, tg, tg);
      ctx.globalAlpha = p.puntoAlfa[0] + p.puntoAlfa[1] * vv;
      ctx.fillStyle = rgba(on || vivo ? cPuntoVivo : cPunto, p.puntoRelleno[0] + fo * p.puntoRelleno[1], fx);
      ctx.beginPath();
      ctx.arc(px[i], py[i], ((on ? p.puntoRadio.aprendido : p.puntoRadio.base) + fo * p.puntoRadio.fogo) * esc300 * ps[i] * vv, 0, 6.2832);
      ctx.fill();
    }

    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }

  function encender(progreso, silencioso) {
    var nuevos = [];
    if (!(progreso >= 0)) return nuevos;           // NaN, undefined o negativo: nada
    for (var j = 0; j < N; j++) {
      var nd = nodos[j];
      if (nd.on || nd.umbral > progreso) continue;
      nd.on = true;
      aprendidos.push(j);
      nuevos.push(j);
      if (silencioso) continue;                    // ya estaba aprendido antes de abrir la pantalla
      nd.fogo = 1;
      if (!calma) { sembrar(j); sembrar(j); }      // al encenderse sale un par de señales
    }
    return nuevos;
  }

  function rafaga() {
    if (calma) return;
    // Sin ningún nodo aprendido todavía, la ráfaga sale de uno cualquiera.
    var desde = aprendidos.length ? aprendidos[Math.random() * aprendidos.length | 0] : null;
    for (var j = 0; j < 3; j++) sembrar(desde);
  }

  function destello() {
    for (var j = 0; j < aprendidos.length; j++) nodos[aprendidos[j]].fogo = 1;
  }

  return { dibujar: dibujar, encender: encender, rafaga: rafaga, destello: destello };
}
